import {
    seeProjectPrice,
    userCanSeeBestPrice,
    userCanSeeBestPriceOnProductCard
} from '@ab-core-functions/user-permission';
import type { ProductWithDetails } from '@ab-core/graphql/dist';
import { useBestPrices, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import type { DefaultScalableProductCardProps } from '../cardLayout';
import { isItShopProduct } from './';
import { fallBackPosition, fallBackQuantityUnit, fallBackSalesUnit } from './fallBackStates';

export const useMappedProductToScalableProductV2 = (
    products?: Array<ProductWithDetails>
): Array<DefaultScalableProductCardProps['product']> => {
    const { activeCart } = getActiveCart();
    const hasBestPrice = userCanSeeBestPrice();
    const hasBestPriceOnProductCard = userCanSeeBestPriceOnProductCard();
    let projectNumber =
        activeCart?.custom.customFieldsRaw.find((element) => element.name === 'cart-projectNr')?.value || '0';
    const canSeeProjectPrice = seeProjectPrice();
    projectNumber = canSeeProjectPrice ? projectNumber : '0';
    const skus: Array<string> = [];
    products?.map((product) => skus.push(product.sku || ''));
    const { bestPrices } = useBestPrices(skus, !hasBestPrice || !hasBestPriceOnProductCard);
    const productList: Array<DefaultScalableProductCardProps['product']> = [];
    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        {
            skus,
            cartId: activeCart?.id
        },
        !activeCart
    );

    products?.map((product) => {
        const productAvailability = productsAvailabilitiesWithChannel?.find(
            (available) => available.sku === product.sku
        );
        const isItShop = isItShopProduct(product.supplierId, productAvailability?.aggregatedMainChannelQuantity);

        const customDeliveryInfo = product.isRewardProduct
            ? (productAvailability?.aggregatedMainChannelQuantity || 0) < 1
                ? 'Diese Prämie wird zeitnah für Sie beschafft.'
                : ''
            : undefined;

        const maxQuantity = product.isLimitedQuantity ? productAvailability?.aggregatedMainChannelQuantity : undefined;

        productList.push({
            defaultQuantity: product.salesUnit || fallBackSalesUnit,
            sku: product.sku || '',
            information: { reach: product.reachInfo },
            isRewardProduct: product.isRewardProduct,
            tags: {
                product: {
                    isAbakus: product.bonus?.isAbakus,
                    isAbakusPlus: product.bonus?.isAbakusPlus,
                    topProduct: product.bonus?.isTopProduct,
                    isTecselect: product.bonus?.isTecselect,
                    isPromotion: product.bonus?.isPromotion,
                    scoreCardIndex: product.scoreCardIndex,
                    isFiveYearGuarantee: product.fiveYearGuarantee,
                    is125Premium: product?.bonus?.isPremiumProduct,
                    is125Vip: product?.bonus?.isVipProduct,
                    isAdditionalSalesPromotion: product?.bonus?.isAdditionalSalesPromotion,
                    successorProduct: product?.successorProduct,
                    ean: product.ean,
                    isItShop
                }
            },
            ids: {
                articleNumber: product.sku,
                ean: product.ean,
                customerArticleNumber: product.customerArticleNumber
            },
            image: {
                imgSrc: product.thumbnail || '',
                sku: product.sku,
                position: fallBackPosition
            },
            name: { text: product.productName, sku: product.sku, position: fallBackPosition },
            additionalLinks: {
                sku: product.sku,
                hasAccessories: product.hasAccessories,
                hasAlternatives: product.hasAlternatives
            },
            prices: {
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                hidePrice: isItShop
            },
            availability: {
                availability: productAvailability,
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                customDeliveryInfo
            },
            order: {
                bestPrice: bestPrices?.find((bestPrice) => bestPrice.sku === product.sku),
                packagingSize: product?.packagingSize || '',
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                sku: product.sku || '',
                isUnqualifiedContractProduct: product.isUnqualifiedContractProduct || false,
                position: fallBackPosition,
                cartId: activeCart?.id,
                supplierId: product.supplierId,
                stockQuantity: productAvailability?.aggregatedMainChannelQuantity ?? 0,
                successorProduct: product.successorProduct || undefined,
                ean: product.ean || '',
                hidePrice: isItShop,
                maxQuantity,
                projectNumber
            },
            comparison: {
                sku: product.sku,
                title: product.productName,
                imageSrc: product.thumbnail || ''
            }
        });
    });

    return productList;
};
